import React from 'react';
import { Box, Typography, Paper, TextField, Button, Avatar, Stack, Table, TableHead, TableRow, TableCell, TableBody, Checkbox, Switch, Tabs, Tab, IconButton, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import api from '../../services/api';
import RichTextEditor from '../common/RichTextEditor';
import EmailLogsList from './EmailLogsList';

type EmailEvents = {
  new_project?: boolean;
  new_offer?: boolean;
  new_subcontractor?: boolean;
};

const EmailNotificationsTab: React.FC = () => {
  const [loading, setLoading] = React.useState(false);
  const [tab, setTab] = React.useState(0);
  const [users, setUsers] = React.useState<any[]>([]);
  const [emailFrom, setEmailFrom] = React.useState('powiadomienia@unitresco.com');
  const [matrix, setMatrix] = React.useState<Record<string, EmailEvents>>({});
  const [enabledUserIds, setEnabledUserIds] = React.useState<string[]>([]);
  const [footer, setFooter] = React.useState<string>('');
  const [templates, setTemplates] = React.useState<Record<string, { subject: string; body: string }>>({});
  const [editKey, setEditKey] = React.useState<string | null>(null);
  const [editSubject, setEditSubject] = React.useState('');
  const [editBody, setEditBody] = React.useState('');

  const load = async () => {
    setLoading(true);
    try {
      const [settingsRes, usersRes] = await Promise.all([
        api.get('/notifications/email/settings'),
        api.get('/users'),
      ]);
      const data = settingsRes.data?.data || {};
      const usersData = usersRes.data?.data || [];
      setUsers(usersData);
      setEmailFrom(data.email_from || 'powiadomienia@unitresco.com');
      const subs = data.email_subscriptions || {};
      setMatrix(subs);
      setEnabledUserIds(Object.keys(subs).filter(uid => Object.values(subs[uid] || {}).some(Boolean)));
      setFooter(data.email_footer_html || '');
      setTemplates(data.email_templates || {});
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => { load(); }, []);

  const toggleUser = (id: string) => {
    setEnabledUserIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    setMatrix(prev => {
      const base = prev[id] || { new_project: false, new_offer: false, new_subcontractor: false };
      return { ...prev, [id]: { ...base } };
    });
  };

  const toggleCell = (userId: string, key: keyof EmailEvents) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setMatrix(prev => ({
      ...prev,
      [userId]: { ...(prev[userId] || { new_project: false, new_offer: false, new_subcontractor: false }), [key]: e.target.checked }
    }));
  };

  const save = async () => {
    setLoading(true);
    try {
      await api.put('/notifications/email/settings', {
        email_from: emailFrom,
        email_subscriptions: matrix,
        email_footer_html: footer,
        email_templates: templates,
      });
    } finally {
      setLoading(false);
    }
  };

  const openEdit = (key: string) => {
    const t = templates[key] || { subject: '', body: '' };
    setEditKey(key);
    setEditSubject(t.subject || '');
    setEditBody(t.body || '');
  };
  const saveEdit = () => {
    if (!editKey) return;
    setTemplates(prev => ({ ...prev, [editKey]: { subject: editSubject, body: editBody } }));
    setEditKey(null);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: '#004273' }}>Powiadomienia e-mail</Typography>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Matryca powiadomień" />
        <Tab label="Adres e-mail" />
        <Tab label="Stopka" />
        <Tab label="Treść wiadomości" />
        <Tab label="Lista powiadomień" />
      </Tabs>
      {tab === 0 && (
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle1" sx={{ mb: 1 }}>Matryca subskrypcji e-mail</Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Użytkownik \\ Zdarzenie</TableCell>
              {[
                { key: 'new_project', label: 'Nowy projekt' },
                { key: 'new_offer', label: 'Nowa oferta' },
                { key: 'new_subcontractor', label: 'Nowy podwykonawca' },
              ].map(col => (
                <TableCell key={col.key} align="center">{col.label}</TableCell>
              ))}
              <TableCell align="center">Włącz</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map(u => (
              <TableRow key={u.user_id}>
                <TableCell>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Avatar src={u.avatar_url} sx={{ width: 22, height: 22 }}>
                      {(u.display_name || u.email)?.charAt(0)}
                    </Avatar>
                    <Typography variant="body2">{u.display_name || `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email}</Typography>
                  </Stack>
                </TableCell>
                {['new_project','new_offer','new_subcontractor'].map(key => (
                  <TableCell key={key} align="center">
                    <Checkbox size="small" disabled={!enabledUserIds.includes(u.user_id)} checked={Boolean(matrix[u.user_id]?.[key as keyof EmailEvents])} onChange={toggleCell(u.user_id, key as keyof EmailEvents)} />
                  </TableCell>
                ))}
                <TableCell align="center">
                  <Switch size="small" checked={enabledUserIds.includes(u.user_id)} onChange={() => toggleUser(u.user_id)} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
      )}
      {tab === 1 && (
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <TextField label="Adres nadawcy" value={emailFrom} onChange={e => setEmailFrom(e.target.value)} size="small" fullWidth />
        </Stack>
      </Paper>
      )}
      {tab === 2 && (
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle1" sx={{ mb: 1 }}>Stopka</Typography>
        <RichTextEditor value={footer} onChange={setFooter} />
      </Paper>
      )}
      {tab === 3 && (
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle1" sx={{ mb: 1 }}>Szablony zdarzeń</Typography>
        {['project.created','offer.created','subcontractor.created'].map(key => (
          <Stack key={key} direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
            <Typography variant="body2">{key}</Typography>
            <IconButton size="small" onClick={() => openEdit(key)}><MoreVertIcon fontSize="small" /></IconButton>
          </Stack>
        ))}
      </Paper>
      )}
      {tab === 4 && (
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <EmailLogsList />
      </Paper>
      )}
      <Button variant="contained" onClick={save} disabled={loading} sx={{ mt: 2 }}>Zapisz</Button>
      <Dialog open={!!editKey} onClose={() => setEditKey(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Edytuj szablon: {editKey}</DialogTitle>
        <DialogContent>
          <TextField label="Temat" value={editSubject} onChange={e => setEditSubject(e.target.value)} fullWidth sx={{ mb: 2 }} />
          <Typography variant="subtitle2" sx={{ mb: 1 }}>{"Treść (WYSIWYG; obsługuje {{zmienne}})"}</Typography>
          <RichTextEditor value={editBody} onChange={setEditBody} />
          <Typography variant="caption" color="text.secondary">Dostępne zmienne zależą od zdarzenia; przykłady: project_id, project_name, offer_id, contractor_name, name.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditKey(null)}>Anuluj</Button>
          <Button variant="contained" onClick={saveEdit}>Zapisz</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EmailNotificationsTab;


